import Vue from 'vue';
import {usesUnifiedAuth,unifiedLogout} from '../api/unifiedAuth';
import Vuex from 'vuex';
import chatStore from './chatStore.js';
import friendStore from './friendStore.js';
import userStore from './userStore.js';
import groupStore from './groupStore.js';
import configStore from './configStore.js';
import uiStore from './uiStore.js';
import { MESSAGE_TYPES } from '@/utils/HappyIframeBridge';

Vue.use(Vuex)

/**
 * 通知 happy iframe 退出登录，等待 ACK 确认
 */
function notifyHappyLogout() {
	return new Promise((resolve) => {
		// 直接查找 iframe 元素
		const iframe = document.querySelector('.agent-iframe');
		if (!iframe || !iframe.contentWindow) {
			console.log('[Store] iframe 未加载，跳过退出登录通知');
			resolve();
			return;
		}
		
		// 获取 happy 应用的 URL（不依赖 iframe.src，因为可能被清空）
		const targetOrigin = process.env.NODE_ENV === 'production' 
			? 'https://happy.shadanai.com' 
			: 'http://localhost:8081';
		
		// 如果有 bridge，使用 bridge 监听 ACK
		const bridge = window.__imHappyIframeBridge;
		let messageId = null;
		let ackReceived = false;
		let timeoutId = null;
		
		// 监听 ACK 确认（如果 bridge 存在）
		const ackHandler = bridge ? (data) => {
			if (data.messageId === messageId) {
				ackReceived = true;
				if (timeoutId) clearTimeout(timeoutId);
				bridge.off('ack', ackHandler);
				console.log('[Store] ✅ 收到退出登录确认');
				resolve();
			}
		} : null;
		
		if (bridge && ackHandler) {
			bridge.on('ack', ackHandler);
		}
		
		// 直接通过 postMessage 发送消息
		try {
			const message = {
				type: MESSAGE_TYPES.USER_LOGOUT,
				data: {
					timestamp: Date.now()
				},
				timestamp: Date.now(),
				messageId: messageId = `logout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
			};
			
			iframe.contentWindow.postMessage(message, targetOrigin);
			console.log('[Store] 已发送退出登录消息，等待确认...');
			
			// 设置超时（2秒），避免无限等待
			timeoutId = setTimeout(() => {
				if (!ackReceived) {
					if (bridge && ackHandler) {
						bridge.off('ack', ackHandler);
					}
					console.warn('[Store] 等待退出登录确认超时');
					resolve();
				}
			}, 2000);
		} catch (error) {
			console.warn('[Store] 发送退出登录消息失败:', error);
			if (bridge && ackHandler) {
				bridge.off('ack', ackHandler);
			}
			resolve();
		}
	});
}

export default new Vuex.Store({
	modules: { chatStore, friendStore, userStore, groupStore, configStore, uiStore },
	state: {},
	mutations: {
		// 全局clear mutation，清理所有模块的状态
		clear(state) {
			console.log('[Store] Executing global clear mutation');
			
			// 清理各个模块的状态
			if (state.userStore) {
				this.commit('userStore/clear');
			}
			if (state.chatStore) {
				this.commit('chatStore/clear');
			}
			if (state.friendStore) {
				this.commit('friendStore/clear');
			}
			if (state.groupStore) {
				this.commit('groupStore/clear');
			}
			if (state.configStore) {
				this.commit('configStore/clear');
			}
			if (state.uiStore) {
				this.commit('uiStore/clear');
			}
			
			console.log('[Store] All modules cleared');
		}
	},
	getters: {
		// 🔥 全局登录状态getter - 同时检查 store 状态和 token
		isLoggedIn: (state) => {
			const hasStoreFlag = state.userStore.isLoggedIn;
			const hasToken = typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('accessToken');
			// 两者必须同时为真才算已登录
			return hasStoreFlag && hasToken;
		}
	},
	actions: {
		load(context) {
			return this.dispatch("loadUser").then(() => {
				const promises = [];
				promises.push(this.dispatch("loadFriend"));
				promises.push(this.dispatch("loadGroup"));
				promises.push(this.dispatch("loadChat"));
				promises.push(this.dispatch("loadConfig"));
				return Promise.all(promises).then(() => {
					// 数据加载完成后，同步到 RooCode
					this.dispatch('syncToRooCode');
				});
			})
		},
		unload(context) {
			// 修复：指定清空特定模块，避免误清空聊天数据
			// context.commit("clear"); // 这会清空所有模块包括聊天数据
			context.commit("clear");
			context.commit("configStore/clear");
			// 注意：不清空 chatStore，避免意外丢失聊天记录
		},
		
		// 🔥 新增：VSCode用户状态同步
		syncUserState({ commit }, vscodeUserState) {
			console.log('[Store] Syncing VSCode user state:', vscodeUserState);
			
			if (vscodeUserState && (vscodeUserState.userId || vscodeUserState.id)) {
				// 标准化用户信息
				const normalizedUser = {
					...vscodeUserState,
					userId: vscodeUserState.userId || vscodeUserState.id
				};
				
				// 同步用户信息到userStore模块
				commit('setUserInfo', normalizedUser);
				// 设置登录状态
				commit('setIsLoggedIn', true);
				
				// 同步vscode侧的accessToken到sessionStorage，保持vue原有逻辑实现
				sessionStorage.setItem('accessToken', normalizedUser.accessToken);
				// 保存到localStorage（保持现有逻辑）
				localStorage.setItem('userInfo', JSON.stringify(normalizedUser));
				
				console.log('[Store] User state synced successfully, userInfo:', normalizedUser);
			} 
		},
		
		// 🔥 新增：登录action
		login({ commit }, userInfo) {
			console.log('[Store] User login:', userInfo);
			
			if (userInfo) {
				commit('setUserInfo', userInfo);
				commit('setIsLoggedIn', true); // 🔥 确保设置登录状态
				localStorage.setItem('userInfo', JSON.stringify(userInfo));
				// 🔥 确保 accessToken 存在
				if (userInfo.accessToken) {
					sessionStorage.setItem('accessToken', userInfo.accessToken);
				}
			}
		},
		
		// 🔥 新增：登出action
		async logout({ commit }) {
            if(usesUnifiedAuth()){await notifyHappyLogout();await unifiedLogout();commit('clear');return;}
			console.log('[Store] User logout');

			// 通知 happy iframe 清除认证凭证，等待确认
			await notifyHappyLogout();

			commit('clear');
			localStorage.removeItem('userInfo');

			// 🔥 同时清理 sessionStorage
			sessionStorage.removeItem('accessToken');
			sessionStorage.removeItem('refreshToken');
			sessionStorage.removeItem('tokenKey');
			sessionStorage.removeItem('happyToken');
		},
		
		// 同步数据到 RooCode
		syncToRooCode({ state }) {
			try {
				if (!window.vscodeServices?.get('ICommandService')) {
					console.log('[Store] VSCode ICommandService not available');
					return;
				}
				
				const userInfo = state.userStore?.userInfo;
				if (!userInfo?.id) {
					console.log('[Store] No user info available for sync');
					return;
				}
				
				const userId = String(userInfo.id);
				const friends = state.friendStore?.friends || [];
				const groups = state.groupStore?.groups || [];
				
				console.log('[Store] Syncing to RooCode:', {
					userId,
					friendsCount: friends.length,
					groupsCount: groups.length
				});
				
				// 同步用户状态
				window.vscodeServices.get('ICommandService').executeCommand('roo-cline.onUserSwitch', {
					userId: userId,
					userName: userInfo.nickName || userInfo.username,
					// VoidBridge期望terminalNo字段，固定为0（账号隔离标识）
					terminalNo: 0,
					// 同时传递terminal字段，VSCode环境下为2
					terminal: window.terminal ? window.terminal : 2,
					// 传递apiToken
					apiToken: userInfo.apiToken
				}).catch(err => console.warn('Failed to sync user state to RooCode:', err));
				
				// 同步联系人数据
				window.vscodeServices.get('ICommandService').executeCommand('roo-cline.updateImContacts', {
					userId: userId,
					friends: friends.filter(f => !f.deleted),
					groups: groups.filter(g => !g.quit)
				}).catch(err => console.warn('Failed to sync contacts to RooCode:', err));
				
			} catch (error) {
				console.error('[Store] Failed to sync to RooCode:', error);
			}
		}
	},
	strict: process.env.NODE_ENV !== 'production'
})
